// sessionService.js
import { authApiService } from './authService';

function parseSessionLimitError(error, fallbackMessage) {
  if (error.response && error.response.data) {
    const payload = error.response.data;
    const nested = payload.body?.data || payload.data || payload;
    return {
      message:
        payload.body?.message ||
        payload.message ||
        nested.message ||
        fallbackMessage,
      errorCode: nested.errorCode,
      data: nested,
    };
  }

  if (error.message) {
    return { message: error.message };
  }

  return { message: fallbackMessage };
}

class SessionService {
  // Get or create an active session for the scribe interface
  async getOrCreateActiveSession() {
    try {
      const response = await authApiService.get('/sessions/active');
      return response.data;
    } catch (error) {
      console.error('Error getting/creating active session:', error);
      throw parseSessionLimitError(error, 'Unable to load active session');
    }
  }

// Create a new session
async createSession(sessionData) {
    try {
      const response = await authApiService.post('/sessions/create', sessionData);
      
      // Handle different response formats
      if (response.data && response.data.success) {
        return response.data;
      } else if (response.data && response.data.body) {
        // Handle alternative response format
        return {
          success: response.data.body.success,
          message: response.data.body.message,
          body: response.data.body.data
        };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error creating session:', error);
      throw parseSessionLimitError(error, 'Failed to create session');
    }
  }

  // Get all sessions for the current user
  async getUserSessions({ status = 'all', limit = 1000, offset = 0, hasSoapNote, createdBy } = {}) {
    try {
      const params = { status, limit, offset };
      if (hasSoapNote) params.hasSoapNote = hasSoapNote;
      if (createdBy && createdBy !== "all") params.createdBy = createdBy;
      const response = await authApiService.get(`/sessions/user-sessions`, {
        params
      });
      return response.data;
    } catch (error) {
      // Older deployments only accept status=active. Keep login resilient
      // while allowing newer deployments to return full chat history.
      if (status === 'all' && !(createdBy && createdBy !== "all")) {
        try {
          const fallbackParams = { status: 'active', limit, offset };
          if (hasSoapNote) fallbackParams.hasSoapNote = hasSoapNote;
          const fallbackResponse = await authApiService.get(`/sessions/user-sessions`, {
            params: fallbackParams
          });
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Error fetching active session fallback:', fallbackError);
          const statusCode =
            fallbackError?.response?.status || fallbackError?.response?.data?.statusCode;
          if (statusCode === 401 || statusCode === 403) {
            throw parseSessionLimitError(fallbackError, 'Failed to fetch sessions');
          }
          // History is supplementary to the scribe workspace. A server-side
          // history failure must not block /scribe or prevent a new session
          // from being created when the user saves patient details.
          return {
            success: true,
            data: { sessions: [] },
            historyUnavailable: true,
          };
        }
      }
      console.error('Error fetching user sessions:', error);
      throw parseSessionLimitError(error, 'Failed to fetch sessions');
    }
  }

  // Get a specific session with SOAP notes + audio
  async getSession(sessionId) {
    try {
      const response = await authApiService.get(`/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching session:', error);
      throw error;
    }
  }

// Update session
async updateSession(sessionId, updateData) {
  try {
    const response = await authApiService.put(`/sessions/${sessionId}`, updateData);
    
    // Handle different response formats
    if (response.data && response.data.success) {
      return response.data;
    } else if (response.data && response.data.body) {
      // Handle alternative response format
      return {
        success: response.data.body.success,
        message: response.data.body.message,
        body: response.data.body.data
      };
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error updating session:', error);
    
    // Handle different error formats
    if (error.response && error.response.data) {
      throw error.response.data;
    } else if (error.message) {
      throw { message: error.message };
    } else {
      throw { message: 'Unknown error occurred' };
    }
  }
}

  // Delete a session (soft delete)
  async deleteSession(sessionId) {
    try {
      const response = await authApiService.delete(`/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }
 
  // Bulk delete sessions
  async bulkDeleteSessions(sessionIds) {
  try {
    const response = await authApiService.post('/sessions/bulk-delete', { sessionIds });
    return response.data;
  } catch (error) {
    console.error('Error bulk deleting sessions:', error);
    throw error;
  }
}

  // Get session statistics
  async getSessionStats() {
    try {
      const response = await authApiService.get('/sessions/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching session stats:', error);
      throw error;
    }
  }

  // Format a single session for display
  formatSessionForDisplay(session) {
    const sessionId = session.sessionId || session.id;
    return {
      id: sessionId,
      name: session.sessionName,
      sessionName: session.sessionName,
      date: session.visitDate ? new Date(session.visitDate).toLocaleDateString() : '',
      visitDate: session.visitDate,
      status: session.status,
      encounterType: session.encounterType,
      specialty: session.specialty,
      patient: session.patient ? {
        id: session.patient.patientId,
      name: session.patient.name || `${session.patient.firstName || ''} ${session.patient.lastName || ''}`.trim(),
      firstName: session.patient.firstName,
      lastName: session.patient.lastName,
      gender: session.patient.gender,
      age: session.patient.age,
      visitType: session.patient.visitType
      // pronouns: session.patient.pronouns,
      // dateOfBirth: session.patient.dateOfBirth
    
      } : null,
      transcript: session.transcript,
    correctedTranscript: session.correctedTranscript,
    audioUrl: session.audioUrl,
      soapNotes: session.soapNotes || [],
      audioSessions: session.audioSessions || [],
      transcriptions: session.transcriptions || [],
      createdAt: session.createdAt
    };
  }

 formatSessionsList(sessions) {
  return sessions.map(session => ({
    id: session.sessionId || session.id,
    name: session.sessionName,
    sessionName: session.sessionName,
    date: session.visitDate ? new Date(session.visitDate).toLocaleDateString() : '',
    visitDate: session.visitDate,
    status: session.status,
    patient: session.patient ? {
      name: session.patient.name || `${session.patient.firstName || ''} ${session.patient.lastName || ''}`.trim(),
      gender: session.patient.gender,
      age: session.patient.age,
      visitType: session.patient.visitType
    } : null,
    hasSoapNote: !!(session.soapNotes && session.soapNotes.length > 0),
    createdAt: session.createdAt
  }));
}
  
}

export const sessionService = new SessionService();